import { createContext, useContext, useReducer } from 'react';
import type { ReactNode } from 'react';
import { FORM_CLEAR_ERROR, FORM_SET_ERROR } from './actionTypes';
import type { FormErrorState, FormErrorAction } from '@/types';

const FormErrorContext = createContext<FormErrorState | null>(null);

const FormErrorDispatchContext = createContext<React.Dispatch<FormErrorAction> | null>(null);

interface FormErrorProviderProps {
  children: ReactNode;
}

export function FormErrorProvider({ children }: FormErrorProviderProps) {
  const [formError, dispatch] = useReducer(formErrorReducer, {});

  return (
    <FormErrorContext.Provider value={formError}>
      <FormErrorDispatchContext.Provider value={dispatch}>
        {children}
      </FormErrorDispatchContext.Provider>
    </FormErrorContext.Provider>
  );
}

export function useFormError(): FormErrorState {
  const formError = useContext(FormErrorContext);
  if (!formError) {
    throw new Error('useFormError must be used within FormErrorProvider');
  }
  return formError;
}

export function useFormErrorDispatch(): React.Dispatch<FormErrorAction> {
  const dispatch = useContext(FormErrorDispatchContext);
  if (!dispatch) {
    throw new Error('useFormErrorDispatch must be used within FormErrorProvider');
  }
  return dispatch;
}

function formErrorReducer(_formError: FormErrorState, action: FormErrorAction): FormErrorState {
  switch (action.type) {
    case FORM_SET_ERROR: {
      return {
        error: action.payload,
      };
    }
    case FORM_CLEAR_ERROR: {
      return {};
    }
    default: {
      throw Error('Unknown action: ' + action);
    }
  }
}
